import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from '@asgardeo/auth-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Payment, { startPayHerePayment } from '../Payment/Payment';
import AuthConfirmModal from './AuthConfirmModal';

import PropTypes from 'prop-types';
import axios from 'axios';
import ProgressView from './views/ProgressView';
import EstimateView from './views/EstimateView';
import ResultView from './views/ResultView';
import UplodeView from './views/UplodeView';
import TermsView from './views/TermsView';
import useFileUpload from './hooks/useFileUpload';
import useUploadAuthEffects from './hooks/useUploadAuthEffects';
import useUploadCallbacks from './hooks/useUploadCallbacks';
import usePaymentHandlers from './hooks/usePaymentHandlers';
import { calculatePrice, getPerWordRate } from '../../config/priceConfig';
import { useCredit } from '../../config/creditContext';

const Upload = ({ onUploadSuccess, onViewStateChange }) => {
  const { state, signIn, getAccessToken, getBasicUserInfo } = useAuthContext();
  const { userId, email: userEmail } = useCredit();
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedType, setSelectedType] = useState('machine');
  const [translationTask, setTranslationTask] = useState(null);
  const [, setRateTask] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [translatedFile, setTranslatedFile] = useState(null);
  const [viewState, setViewState] = useState('upload');
  const [showPayment, setShowPayment] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isFullyAuthenticated, setIsFullyAuthenticated] = useState(false);
  const [userInfoError, setUserInfoError] = useState(false);
  const [, setUserInfo] = useState(null);
  const [creditEstimate, setCreditEstimate] = useState(null);

  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [, setLoadingTranslate] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [translationStatus, setTranslationStatus] = useState('');

  // Critical fix: Use refs for better polling control
  const ratePollingRef = useRef(null);
  const translatePollingRef = useRef(null);
  const retryCountRef = useRef(0);
  const isPollingRef = useRef(false); // Prevent duplicate polling
  const lastRequestTimeRef = useRef(0); // Rate limiting
  const requestInFlightRef = useRef(false); // Prevent concurrent requests

  const maxRetries = 3; // Reduced from 5
  const MIN_POLL_INTERVAL = 15000;
  const MAX_POLL_INTERVAL = 35000;

  const SUPPORTED_FORMATS = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  let SERVER_CONFIGS = {
    ENV: process.env.REACT_APP_ENV || 'prod',
    TRANSLATE_LOCAL_SERVER:
      process.env.REACT_APP_TRANSLATE_LOCAL_SERVER || 'http://localhost:6500',
    TRANSLATE_PROD_SERVER:
      process.env.REACT_APP_TRANSLATE_PROD_SERVER ||
      'https://www.translate.paralegal.lk/api',
  };

  const ENV = SERVER_CONFIGS.ENV;
  const HOST =
    ENV === 'local'
      ? SERVER_CONFIGS.TRANSLATE_LOCAL_SERVER
      : SERVER_CONFIGS.TRANSLATE_PROD_SERVER;

  // Notify parent component when viewState changes
  useEffect(() => {
    if (onViewStateChange) {
      onViewStateChange(viewState);
    }
  }, [viewState, onViewStateChange]);

  // Move ensureAuthenticated definition above useFileUpload
  const ensureAuthenticated = () => {
    if (!state.isAuthenticated) {
      setIsAuthModalOpen(true);
      return false;
    }
    if (!isFullyAuthenticated) {

      return false;
    }
    return true;
  };

  // Use the custom hook for file upload logic FIRST so filePath is available
  const fileInputRef = useRef(null);
  const {
    uploadedFile,
    uploadProgress,
    filePath,
    dragging,
    handleFileInputChange,
    handleDeleteFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useFileUpload({
    SUPPORTED_FORMATS,
    HOST,
    ensureAuthenticated,
    onUploadSuccess,
    toast,
  });

  // Now use the hooks that depend on filePath
  const {
    clearPolling,
    makeRequest,
    pollTaskStatus,
    startPolling,
  } = useUploadCallbacks({
    ENV,
    HOST,
    setRateTask,
    setEstimate,
    setViewState,
    setLoadingEstimate,
    setTranslationTask,
    setTranslatedFile,
    setTranslationProgress,
    setTranslationStatus,
    setLoadingTranslate,
    toast,
    maxRetries,
    MIN_POLL_INTERVAL,
    MAX_POLL_INTERVAL,
    ratePollingRef,
    translatePollingRef,
    retryCountRef,
    isPollingRef,
    lastRequestTimeRef,
    requestInFlightRef,
    selectedType,
    setCreditEstimate,
  });

  const {
    handlePaymentSuccess,
    handlePaymentClose,
    handlePayNow,
  } = usePaymentHandlers({
    ENV,
    estimate,
    userEmail,
    setShowPayment,
    setPaymentCompleted,
    setViewState,
    setTranslationProgress,
    setTranslationStatus,
    setLoadingTranslate,
    setTranslationTask,
    setTranslatedFile,
    toast,
    filePath,
    selectedLanguage,
    startPolling,
  });

  // Move all useEffect hooks related to authentication, user info, and cleanup to the custom hook
  useUploadAuthEffects({
    state,
    getAccessToken,
    setUserInfoError,
    setUserInfo,
    setIsFullyAuthenticated,
    isFullyAuthenticated,
    userInfoError,
    toast,
    clearPolling,
  });

  // Handle terms modal actions
  const handleShowTerms = () => {
    setShowTermsModal(true);
  };

  const handleTermsAgree = () => {
    // Clear any background polling that might interfere
    clearPolling();
    // Close terms modal
    setShowTermsModal(false);
    // Directly trigger PayHere payment without showing payment modal
    startPayHerePayment({
      amount: Number(estimate?.amount_to_pay),
      wordCount: Number(estimate?.word_count),
      email: userEmail,
      onSuccess: handlePaymentSuccess,
      onError: (error) => {
        toast.error('Payment failed or was declined. Please try again.');
      },
      onClose: handlePaymentClose,
    });
  };

  const handleTermsCancel = () => {
    setShowTermsModal(false);
  };

  const handleGetEstimate = async () => {
    if (!ensureAuthenticated()) return;
    if (!selectedLanguage) {
      toast.error('Please select your source and target language combination.');
      return;
    }
    if (!selectedType) {
      toast.error('Please select your translation type.');
      return;
    }
    if (uploadProgress !== 'success') {
      toast.error('Please wait, your file is uploading.');
      return;
    }
    setLoadingEstimate(true);

    try {

      const doc_id = sessionStorage.getItem('doc_id');
      const response = await makeRequest(`${HOST}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          doc_path: filePath,
          source_to_target: selectedLanguage,
          translation_type: selectedType,
          doc_id: doc_id ? parseInt(doc_id) : null,
          user_id: userId,
        }),
      });

      if (!response.ok) throw new Error('Failed to start rate calculation');
      const data = await response.json();
      setRateTask(data);
      console.log('Estimate API response:', data); // Debug log

      // Support both local and prod response formats
      const isCompleted = data.status === 'COMPLETED' ||
        (data.result && data.result.status === 'COMPLETED');

      if (isCompleted) {
        console.log('DEBUG - isCompleted true', data);
        // Extract result data - handle both formats
        const resultData = data.result || data;

        if (!resultData || typeof resultData.word_count === 'undefined') {
          toast.error('Invalid response from server.');
          setLoadingEstimate(false);
          return;
        }

        const wordCount = Number(resultData.word_count);
        const per_word_rate = Number(getPerWordRate(selectedType));
        const amount_to_pay = Number(calculatePrice(wordCount, selectedType));
        setEstimate({
          word_count: wordCount,
          per_word_rate,
          amount_to_pay,
          estimated_time: resultData.estimated_time,
        });

        // 2. Call your backend to check/update credit
        const user_id = parseInt(sessionStorage.getItem('user_id'));
        const doc_id = sessionStorage.getItem('doc_id');

        console.log('DEBUG - user_id from sessionStorage:', user_id);
        console.log('DEBUG - doc_id from sessionStorage:', doc_id);

        if (!user_id) {
          toast.error('User ID not available. Please refresh and try again.');
          setLoadingEstimate(false);
          return;
        }

        const backendRes = await axios.post(
          `${process.env.REACT_APP_DB_SERVER_URL}/credit/estimate`,
          { user_id, cost: amount_to_pay, wordcount: wordCount, email: userEmail, doc_id: doc_id ? parseInt(doc_id) : null }
        );
        setCreditEstimate(backendRes.data);
        // console.log('DEBUG - Set creditEstimate:', backendRes.data);

        setViewState('estimate');
        setLoadingEstimate(false);

      } else {
        // Start optimized polling for non-completed tasks
        const taskId = data.task_id;
        const status = data.status || (data.result && data.result.status);
        startPolling(taskId, 'rate', status);

      }
    } catch (error) {
      setLoadingEstimate(false);
      // If the error is likely a 500 from the backend, show the page limit error toast
      if (error && error.message && error.message.includes('Failed to start rate calculation')) {
        toast.error('The document may exceed the 30-page limit or another server error occurred.');
      } else {
        toast.error('Failed to get estimate');
      }
      console.error(error);
    }
  };

  const handlePayAndTranslate = () => {


    if (creditEstimate && creditEstimate.need_to_pay > 0) {
      // Show PayHere payment modal
      setShowPayment(true);
    } else {
      // Credit was enough, just translate
      console.log('DEBUG - Calling handleTranslate with order_id:', creditEstimate?.order_id);
      handleTranslate(creditEstimate?.order_id); // <-- Pass the order_id explicitly!
    }
  };

  const handleTranslate = async (overrideOrderId) => {
    if (!ensureAuthenticated()) {
      return;
    }

    setLoadingTranslate(true);

    // Only set progress states if not already set (to avoid overriding payment flow states)
    if (viewState !== 'progress') {
      setViewState('progress');
      setTranslationProgress(5);
      setTranslationStatus('Starting');
    }

    try {
      // Get user info from session storage
      const userData = sessionStorage.getItem('user');
      if (!userData) {
        // If userData is not found in session storage, try to fetch it again
        try {
          const accessToken = await getAccessToken();
          if (!accessToken) {
            throw new Error('Failed to get access token');
          }

          // User is authenticated but we don't have their info
          // Call the fetchUserInfo function again
          throw new Error('User data not found. Please try again.');
        } catch (error) {
          toast.error('Authentication error. Please sign in again.');
          signIn();
          setLoadingTranslate(false);
          return;
        }
      }

      const userInfoData = JSON.parse(userData);

      // Get the access token - either from stored data or fresh
      let accessToken = userInfoData.authToken;

      // If token is expired or missing, get a new one
      if (
        !accessToken ||
        (userInfoData.tokenExpiry && userInfoData.tokenExpiry < Date.now())
      ) {
        console.log('Access token expired or missing, getting a new one');
        try {
          accessToken = await getAccessToken();

          // Update the stored user data with the new token
          userInfoData.authToken = accessToken;
          userInfoData.tokenExpiry = Date.now() + 3600 * 1000; // 1 hour from now
          sessionStorage.setItem('user', JSON.stringify(userInfoData));
        } catch (error) {
          console.error('Failed to refresh access token:', error);
          toast.error('Authentication expired. Please sign in again.');
          signIn();
          setLoadingTranslate(false);
          return;
        }
      }

      // Use sub from Asgardeo as auth_id if available, otherwise fall back to stored authId
      const auth_id = userInfoData.sub || userInfoData.authId || '';

      const orderId = overrideOrderId || creditEstimate?.order_id;

      // Add these debug logs:
      console.log('DEBUG - overrideOrderId:', overrideOrderId);
      console.log('DEBUG - creditEstimate:', creditEstimate);
      console.log('DEBUG - creditEstimate?.order_id:', creditEstimate?.order_id);
      console.log('DEBUG - final orderId:', orderId);

      if (!orderId || typeof orderId !== "string") {
        console.log('DEBUG - Order ID validation failed. orderId:', orderId, 'type:', typeof orderId);
        toast.error("Order ID missing. Please get an estimate or complete payment.");
        setLoadingTranslate(false);
        setTranslationStatus('Failed to start');
        return;
      }

      const doc_id = sessionStorage.getItem('doc_id');
      const response = await makeRequest(`${HOST}/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          doc_path: filePath,
          source_to_target: selectedLanguage,
          order_id: orderId,
          user_id: userId,
          doc_id: doc_id ? parseInt(doc_id) : null,
        }),
      });

      if (!response.ok) throw new Error('Failed to start translation');

      const result = await response.json();
      setTranslationTask(result);

      console.log('Translation task:', result);
      startPolling(result.task_id, 'translate', result.status);
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Translation failed to start');
      setLoadingTranslate(false);
      setTranslationStatus('Failed to start');
    }
  };

  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [loadingText, setLoadingText] = useState(false);


  const getTitleForView = (viewState) => {
    switch (viewState) {
      case 'upload':
        return 'Upload your document';
      case 'estimate':
        return 'Translation estimate';
      case 'progress':
        return 'Translation in progress';
      case 'result':
        return 'Translation complete';
      default:
        return 'Translation Service';
    }
  };

  const handleViewText = async () => {
    setLoadingText(true);
    try {
      const response = await fetch(
        `${HOST}/download?file=${encodeURIComponent(translatedFile?.textPath)}`
      );
      if (!response.ok) throw new Error('Failed to fetch text');
      const text = await response.text();
      setTextContent(text);
    } catch (err) {
      toast.error('Could not load text.');
    }
    setLoadingText(false);
  };

  return (
    <>
      <AuthConfirmModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onConfirm={() => {
          setIsAuthModalOpen(false);
          signIn();
        }}
      />
      <div className="font-poppins px-6">
        <div className="relative">
          <h2 className="text-3xl font-semibold text-center text-gray-500 ">

            {getTitleForView(viewState)}
          </h2>

          {viewState === 'upload' && <UplodeView
            uploadedFile={uploadedFile}
            uploadProgress={uploadProgress}
            handleDeleteFile={handleDeleteFile}
            dragging={dragging}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            ensureAuthenticated={ensureAuthenticated}
            state={state}
            isFullyAuthenticated={isFullyAuthenticated}
            fileInputRef={fileInputRef}
            handleFileInputChange={handleFileInputChange}
            SUPPORTED_FORMATS={SUPPORTED_FORMATS}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            toast={toast}
          />}
          {viewState === 'estimate' && (
            <EstimateView
              estimate={estimate}
              creditEstimate={creditEstimate}
              loadingEstimate={loadingEstimate}
              onPayAndTranslate={handlePayAndTranslate}
              onTranslate={() => handlePaymentSuccess(creditEstimate.order_id)} //here send credit Order_ID
            />
          )}
          {viewState === 'progress' && <ProgressView translationProgress={translationProgress} translationStatus={translationStatus} translationTask={translationTask} />}
          {viewState === 'result' && <ResultView
            translatedFile={translatedFile}
            HOST={HOST}
            handleViewText={handleViewText}
            loadingText={loadingText}
            showPdfViewer={showPdfViewer}
            setShowPdfViewer={setShowPdfViewer}
            textContent={textContent}
            setTextContent={setTextContent}
          />}

          {viewState === 'upload' && uploadedFile && uploadProgress === 'success' && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleGetEstimate}
                disabled={loadingEstimate}
                className="flex justify-center items-center px-8 py-2 rounded-full text-sm font-bold bg-pl_prim_color_1 text-white hover:bg-pl_prim_color_2 transition-all duration-300 hover:shadow-md transform hover:-translate-y-0.5 shadow-sm disabled:opacity-50 disabled:pointer-events-none"
              >
                {loadingEstimate ? (
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v2a6 6 0 00-6 6H4z"
                    ></path>
                  </svg>
                ) : (
                  'Get estimate'
                )}
              </button>
            </div>
          )}
        </div>
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          style={{ zIndex: 9999 }}
          toastStyle={{
            backgroundColor: '#1E1E1E',
            color: '#FFFFFF',
            border: '1px solid #333',
          }}
        />

        {/* Terms Modal */}
        <TermsView
          open={showTermsModal}
          onAgree={handleTermsAgree}
          onCancel={handleTermsCancel}
        />
        {showPayment && creditEstimate && creditEstimate.need_to_pay > 0 && (
          <Payment
            amount={creditEstimate.need_to_pay}
            wordCount={estimate.word_count}
            email={userEmail}
            onSuccess={(payHereOrderId) => {
              setShowPayment(false);
              handlePaymentSuccess(payHereOrderId);
            }}
            onClose={() => setShowPayment(false)}
          />
        )}
      </div>
    </>
  );
};

Upload.propTypes = {
  onClose: PropTypes.func,
  onUploadSuccess: PropTypes.func.isRequired,
  onViewStateChange: PropTypes.func,
};

export default Upload;
